import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useJobs } from '../../hooks/useJobs';
import JobsTable from './JobsTable';
import JobForm from './JobForm';
import ApplicationsTable from './ApplicationsTable';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  const { jobs, loading, error, refetch } = useJobs();
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'jobs' | 'applications'>('jobs');
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/admin');
  };

  const handleEdit = (job: any) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingJob(null);
    // Explicitly refetch jobs to ensure the list is updated
    // Use a small delay to ensure the form has closed and any pending operations are complete
    setTimeout(() => {
      refetch();
    }, 100);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <div className="loading-spinner-large"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="admin-welcome">
              Welcome, {user?.email}
            </p>
          </div>
          <div className="admin-header-actions">
            <button onClick={handleCreate} className="admin-btn-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="5" x2="12" y2="19" strokeWidth="2"/>
                <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2"/>
              </svg>
              Add New Job
            </button>
            <button onClick={handleLogout} className="admin-btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          Jobs
        </button>
        <button
          className={`admin-tab ${activeTab === 'applications' ? 'active' : ''}`}
          onClick={() => setActiveTab('applications')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <polyline points="17 11 19 13 23 9"/>
          </svg>
          Applications
        </button>
      </div>

      <div className="admin-content">
        {error && activeTab === 'jobs' && (
          <div className="admin-error-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
            </svg>
            {error}
          </div>
        )}

        {activeTab === 'jobs' && (
          <JobsTable jobs={jobs} onEdit={handleEdit} onRefresh={refetch} />
        )}

        {activeTab === 'applications' && (
          <ApplicationsTable />
        )}

        {showForm && (
          <JobForm
            job={editingJob}
            onClose={handleFormClose}
            onSuccess={handleFormClose}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

